import React from 'react';
import {Box} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import type {MenuOption} from '@contentlab/components/actions-menu/ActionsMenu';
import DropdownButton from '@contentlab/components/dropdown-button/DropdownButton';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import type {AvatarFilter} from '@contentlab/types/avatarFilters';

interface AvatarFiltersBarProps {
  filters: AvatarFilter[];
  onFilterChange: (id: string, selectedOptionId: string[]) => void;
}

function AvatarFiltersBar({filters, onFilterChange}: AvatarFiltersBarProps) {
  const handleFilterChange = (
    filterId: string,
    selectedOptionIds: string[]
  ) => {
    onFilterChange(filterId, selectedOptionIds);
  };

  const handleFilterOptionClicked = (
    filter: AvatarFilter,
    optionId: string
  ) => {
    const isSelected = filter.selectedOptionId.includes(optionId);
    let newSelectedOptionId: string[];
    if (isSelected) {
      newSelectedOptionId = filter.selectedOptionId.filter(
        (id) => id !== optionId
      );
    } else {
      newSelectedOptionId = [...filter.selectedOptionId, optionId];
    }
    handleFilterChange(filter.id, newSelectedOptionId);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '4px',
        width: '100%',
        overflowX: 'auto',
        ...scrollbarMixins.overrideScrollbarHover(true, 3),
      }}
    >
      {filters.map((filter) => {
        const menuOptions: MenuOption[] = filter.options.map((option) => ({
          id: option.id,
          title: option.label,
          onClick: () => {
            handleFilterOptionClicked(filter, option.id);
          },
        }));

        return (
          <DropdownButton
            key={filter.id}
            testID={`filter-${filter.id}`}
            onClick={(element: HTMLElement) => element?.scrollIntoView()}
            style={{
              padding: '4px',
              borderColor: filter.selectedOptionId.length
                ? designSystemToken('semantic.fg.primary')
                : undefined,
              backgroundColor: filter.selectedOptionId.length
                ? designSystemToken('semantic.bg.alpha')
                : undefined,
            }}
            label={filter.label}
            labelStyle={{
              whiteSpace: 'nowrap',
              paddingInline: '6px',
            }}
            menuOptions={menuOptions}
            selectedOptionId={filter.selectedOptionId}
            multiple
            showSelectedAmount
            onClearButtonClick={
              filter.selectedOptionId.length > 0
                ? () => handleFilterChange(filter.id, [])
                : undefined
            }
          />
        );
      })}
    </Box>
  );
}

export default AvatarFiltersBar;
